import { HttpErrorResponse } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { Observable, throwError } from 'rxjs';
import { AuthSnapshotService } from 'src/app/store/auth/auth-snapshot.service';

/** Sign-in pages own their 401s — bouncing a rejected sign-in off the sign-in page would loop. */
const AUTH_PAGES = ['/login', '/guest-login', '/no-access', '/reset-password'];

/**
 * Routes served to email-invited guests, who sign in with an access token instead of a password.
 * The two legacy supplier paths redirect to the portal before any request fires, but are kept so a
 * 401 raised mid-redirect still goes to the guest sign-in.
 */
const GUEST_PAGES = ['/supplier-portal', '/rfq-submission', '/po-submission', '/guest-qc-issues'];

@Injectable({
  providedIn: 'root'
})
export class ErrorHandlerService {

  /** Parallel requests fail together; without this each 401 would queue its own navigation. */
  private isRedirecting = false;

  /**
   * Reads the session through the store. This used to go straight to localStorage
   * because AuthService injects this service, so depending back on it would have
   * closed a DI cycle. AuthSnapshotService depends only on Store, which breaks the
   * cycle structurally instead of routing around it.
   */
  constructor(
    private router: Router,
    private toastr: ToastrService,
    private auth: AuthSnapshotService
  ) { }

  /**
   * The function handles HTTP errors by displaying an error message and logging the error, then
   * returning an observable that throws the error message.
   * @param {HttpErrorResponse} e - HttpErrorResponse - The error response object returned by the HTTP
   * request.
   * @returns an Observable that emits an error with the error message.
   */
  handleHttpError(e: HttpErrorResponse): Observable<never> {
    // console.log(e)
    let errorMessage = `Error: ${e.status} (${e.statusText})`;

    if (this.handleUnauthorised(e)) return throwError(errorMessage);

    if (e.error.error === 'Forbidden') {
      // Hide blocking memo related API warning
      if(e.error.message.includes("memo")){
        // this.toastr.warning("You have unacknowledged memos that block access to the application");
      } else {
        this.toastr.error('This account do not have the necessary permission to access the data from ' + e.url, errorMessage);
      }
    } else {
      if (Array.isArray(e.error.message)) {
        this.toastr.error(e.error.message.join(" "), errorMessage);
      } else {
        this.toastr.error(e.error.message, errorMessage);
      }

      console.error(e);
    }

    return throwError(errorMessage);
  }

  /**
   * A 401 arrives in two shapes and a toast serves neither: the request carried no token at all
   * because nobody is signed in, or it carried one the backend rejected as expired, revoked, or
   * belonging to someone else. Nobody-signed-in goes straight to the login page; a rejected token
   * goes to the no-access page, which explains itself and offers the way back in.
   *
   * Either way the current URL rides along as returnUrl, so a supplier who opens an emailed RFQ link
   * before signing in still lands on that RFQ afterwards.
   *
   * @returns true when this took over, so the caller skips the generic toast.
   */
  private handleUnauthorised(e: HttpErrorResponse): boolean {
    if (e.status !== 401) return false;

    const currentUrl = this.router.url;
    if (AUTH_PAGES.some(page => currentUrl.startsWith(page))) return false;
    if (this.isRedirecting) return true;

    const isGuestPage = GUEST_PAGES.some(page => currentUrl.startsWith(page));
    const loginRoute = isGuestPage ? '/guest-login' : '/login';
    const hasToken = !!this.auth.token;

    this.isRedirecting = true;
    const navigation = hasToken
      ? this.router.navigate(['/no-access'], {
          queryParams: { login: isGuestPage ? 'guest' : 'staff', message: 'session-expired', returnUrl: currentUrl }
        })
      : this.router.navigate([loginRoute], { queryParams: { returnUrl: currentUrl } });

    navigation.finally(() => this.isRedirecting = false);
    return true;
  }
}
